import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID } from '@adornis/baseql/baseqlTypes.js';
import { Field } from '@adornis/baseql/decorators.js';
import { Entity } from '@adornis/baseql/decorators/entity.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { PersonImportData } from './PersonImportConvertConfig.js';

/** the stage of the matcher that decided a row - see `getPersonImportMatch` */
export type PersonImportMatchedBy = 'resolvedId' | 'externalUniqueID' | 'debitorennummer' | 'email' | 'name';

/**
 * The specifier that links a guardian to their children. Shared with the client, which shows it as a
 * count rather than as the list of person ids it holds.
 */
export const CHILD_ID_FIELD_SPECIFIER_ID = 'Sorgeberechtigung für Kinder';

export enum FieldType {
  STRING = 'STRING',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  BOOLEAN = 'BOOLEAN',
}

/**
 * How much attention a finding needs. Numeric so the worst finding of a row can be kept as one
 * sortable value and the overview floats the rows that need a human to the top.
 */
export enum ImportFindingSeverity {
  HINWEIS = 0,
  WARNUNG = 1,
  FEHLER = 2,
}

/**
 * What kind of situation a finding describes. The overview counts and filters by it, so a user can work
 * through all rows of one kind at once - every invalid email, every undecided name - instead of reading
 * row by row.
 */
export enum ImportFindingCode {
  NEAR_MISS = 'NEAR_MISS',
  SELF_REFERENCE = 'SELF_REFERENCE',
  KEY_OF_RELATIVE = 'KEY_OF_RELATIVE',
  KEY_OUTRANKED = 'KEY_OUTRANKED',
  KEY_ROLE_CONTRADICTS = 'KEY_ROLE_CONTRADICTS',
  INVALID_EMAIL = 'INVALID_EMAIL',
  UNKNOWN_KOSTENSTELLE = 'UNKNOWN_KOSTENSTELLE',
  NO_KEYS = 'NO_KEYS',
  NAME_MATCH = 'NAME_MATCH',
  NAME_SWAPPED = 'NAME_SWAPPED',
  NAME_DIFFERS = 'NAME_DIFFERS',
  CHILD_UNSAFE = 'CHILD_UNSAFE',
  DELETES_VALUES = 'DELETES_VALUES',
  MERGED_ROWS = 'MERGED_ROWS',
  NO_BOOKING = 'NO_BOOKING',
  FILE_CONFLICT = 'FILE_CONFLICT',
  DB_DUPLICATES = 'DB_DUPLICATES',
}

/**
 * How a finding code reads in the overview, and how it is fixed right in the table while it is filtered
 * for: `columns` become inputs - the fast path for "correct every faulty email" -, `confirmsPerson` a
 * button that takes the found person as right. Everything else is fixed in the row's edit dialog or by
 * assigning a person.
 */
export const IMPORT_FINDING_CATEGORIES: Record<
  ImportFindingCode,
  { label: string; columns: string[]; confirmsPerson?: boolean }
> = {
  [ImportFindingCode.NEAR_MISS]: { label: 'Nicht eindeutig', columns: [] },
  [ImportFindingCode.SELF_REFERENCE]: { label: 'Schlüssel gehört dem Kind', columns: [] },
  [ImportFindingCode.KEY_OF_RELATIVE]: { label: 'Schlüssel eines Angehörigen', columns: [] },
  [ImportFindingCode.KEY_OUTRANKED]: { label: 'Externe ID und Debitorennr. widersprechen sich', columns: [] },
  [ImportFindingCode.KEY_ROLE_CONTRADICTS]: { label: 'Rolle passt nicht', columns: [] },
  [ImportFindingCode.INVALID_EMAIL]: { label: 'Ungültige E-Mail', columns: ['email'] },
  [ImportFindingCode.UNKNOWN_KOSTENSTELLE]: { label: 'Unbekannte Kostenstelle', columns: ['schule_kostenstelle'] },
  [ImportFindingCode.NO_KEYS]: { label: 'Ohne Schlüssel', columns: [] },
  [ImportFindingCode.NAME_MATCH]: { label: 'Über Namen gefunden', columns: [], confirmsPerson: true },
  [ImportFindingCode.NAME_SWAPPED]: {
    label: 'Vor- und Nachname vertauscht',
    columns: ['stammdaten_vorname', 'stammdaten_nachname'],
  },
  [ImportFindingCode.NAME_DIFFERS]: {
    label: 'Name weicht ab',
    columns: ['stammdaten_vorname', 'stammdaten_nachname'],
  },
  [ImportFindingCode.CHILD_UNSAFE]: { label: 'Kind nicht sicher zugeordnet', columns: [] },
  [ImportFindingCode.DELETES_VALUES]: { label: 'Löscht Werte', columns: [] },
  [ImportFindingCode.MERGED_ROWS]: { label: 'Mehrfach in der Datei', columns: [] },
  [ImportFindingCode.NO_BOOKING]: { label: 'Ohne Klassenbuchung', columns: [] },
  [ImportFindingCode.FILE_CONFLICT]: { label: 'Widersprüchlich in der Datei', columns: [] },
  [ImportFindingCode.DB_DUPLICATES]: { label: 'Doppelt angelegt', columns: [] },
};

/**
 * Something the import noticed about a row that the user should see before executing - a possible
 * duplicate, an unresolvable reference, values that will be deleted. See `collectImportFindings`.
 */
@Entity()
export class ImportFinding extends AdornisEntity {
  public static override _class = 'ImportFinding';

  constructor(doc: PartialEntityData<ImportFinding>) {
    super(doc);
  }

  /**
   * Entities in an array need an id of their own - without it they cannot be merged on the client.
   */
  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() })
  _id: Maybe<string>;

  @Field(() => Float)
  severity: Maybe<ImportFindingSeverity>;

  @Field(() => String)
  code: Maybe<ImportFindingCode>;

  @Field(() => String)
  message: Maybe<string>;
}

/**
 * A cell of an import row as the user corrected it in the overview - the csv column and the value they
 * typed, parsed on the server exactly like the file's cell would have been.
 */
@Entity()
export class ImportCsvCell extends AdornisEntity {
  public static override _class = 'ImportCsvCell';

  constructor(doc: PartialEntityData<ImportCsvCell>) {
    super(doc);
  }

  @Field(() => String)
  column: Maybe<string>;

  @Field(() => String)
  value: Maybe<string>;
}

/**
 * One column that the rows merged into a strategy fill differently - see
 * {@link PersonImportStrategy.fileConflicts}.
 */
@Entity()
export class ImportFileConflict extends AdornisEntity {
  public static override _class = 'ImportFileConflict';

  constructor(doc: PartialEntityData<ImportFileConflict>) {
    super(doc);
  }

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() })
  _id: Maybe<string>;

  /** the PersonImportData field, e.g. `stammdaten_vorname` */
  @Field(() => String)
  field: Maybe<string>;

  /** every distinct value the file holds for it, in file order */
  @Field(() => [String], { default: v => v ?? [] })
  values: Maybe<string[]>;
}

@Entity()
export class ImportFieldSpecifier extends AdornisEntity {
  public static override _class = 'ImportFieldSpecifier';

  constructor(doc: PartialEntityData<ImportFieldSpecifier>) {
    super(doc);
  }

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() })
  _id: Maybe<string>;

  /**
   * The type of the field. We can look this up in the user schema to correctly parse the field in the execution.
   */
  @Field(() => String, { default: v => v ?? FieldType.STRING })
  type: Maybe<FieldType>;

  /**
   * The field name in the CSV file.
   */
  @Field(() => String)
  csvFieldName: Maybe<string>;

  /**
   * The field path in the database.
   */
  @Field(() => String)
  dbFieldPath: Maybe<string>;

  /**
   * The previous value in the database if the matching algorithm found a match. If the value is empty, the field was not present in the database.
   */
  @Field(() => String)
  from: Maybe<string>;

  /**
   * The new value in the csv file.
   */
  @Field(() => String)
  to: Maybe<string>;

  /**
   * Whether the field should be deleted from the database.
   */
  @Field(() => Boolean)
  delete: Maybe<boolean>;
}

@Entity()
export class PersonImportStrategy extends MongoEntity {
  public static override _class = 'PersonImportStrategy';
  public static override _collectionName = 'person-import-strategies';

  constructor(doc: PartialEntityData<PersonImportStrategy>) {
    super(doc);
  }

  /**
   * When was the strategy created. Used for automatic garbage collection per index
   */
  @Field(() => Date, { default: v => v ?? new Date() })
  createdAt!: Date;

  /**
   * There could be multiple import processes running at the same time.
   * This ID references the import process that this strategy belongs to.
   */
  @Field(() => ID)
  importProcessID: Maybe<string>;

  /**
   * The strategies this one depends on. A guardian row depends on the rows of its children, whose person
   * ids it links - several when the file lists the guardian at more than one child. When a child's row
   * changes (another person assigned, a value edited), the guardian is recalculated too.
   */
  @Field(() => [ID], { default: v => v ?? [] })
  dependencyIds: Maybe<string[]>;

  /**
   * The user picked or cleared the person in the overview. Such a row is never matched again - every
   * other row is, on every recalculation, because an edited value or a changed child can change who it is.
   */
  @Field(() => Boolean, { default: v => v ?? false })
  manuallyAssigned: Maybe<boolean>;

  /**
   * Columns the rows merged into this strategy disagree on - the same guardian written two ways at two
   * children. Applying both would flip the stored value on every import, so the row waits until a human
   * picks one by editing it. An edit of a column resolves its conflict.
   */
  @Field(() => [ImportFileConflict], { default: v => v ?? [] })
  fileConflicts: Maybe<ImportFileConflict[]>;

  /**
   * This is the ID of the person to update. If this field is empty the strategy is an insert.
   * In the strategy overview a user might change the strategy from insert to update if he inserts a persons id here.
   */
  @Field(() => ID)
  resolvedId: Maybe<string>;

  /**
   * Further records that are true duplicates of `resolvedId` (same email AND identical name). They are
   * not updated wholesale during execution — only their `sorgeberechtigteKinderIDs` link to the imported
   * student is ensured, so the relationship stays visible on every duplicate record.
   */
  @Field(() => [ID], { default: v => v ?? [] })
  duplicateMatchIDs: Maybe<string[]>;

  /**
   * The resolved person as the overview shows it ("2041187 · Lena Sophie Krämer"), denormalized so the
   * table does not query every row's person. Empty for a row that creates a new person.
   */
  @Field(() => String)
  resolvedLabel: Maybe<string>;

  /**
   * For a guardian row: the names of the children of the file it links - what the user needs to recognise
   * the family without opening the row.
   */
  @Field(() => [String], { default: v => v ?? [] })
  childNames: Maybe<string[]>;

  /**
   * How sure the import is that its decision for this row - update the resolved person, or create a new
   * one - is the right one. A value between 0 and 1: both "this is definitely that person" and
   * "definitely nobody we have" are high, only a row that could go either way is low.
   */
  @Field(() => Float)
  matchConfidence: Maybe<number>;

  /**
   * Which evidence resolved the row to {@link resolvedId} - the overview says so next to the certainty,
   * because "100%" alone does not tell the user whether a key or a name decided it.
   */
  @Field(() => String)
  matchedBy: Maybe<PersonImportMatchedBy>;

  /**
   * The position of the row in the overview, frozen when the strategies are written. The natural order
   * is by severity, but that changes the moment a person is assigned - and a list that reorders under
   * the cursor cannot be worked through. So the order is decided once, on the server, and never again.
   */
  @Field(() => Float)
  sortIndex: Maybe<number>;

  /**
   * Whether the strategy has been approved by the user.
   */
  @Field(() => Boolean, { default: v => v ?? false })
  approved: Maybe<boolean>;

  /**
   * The row resolves to an existing person and not a single specifier differs from what is stored, so
   * executing it would write nothing. Denormalized because mongo cannot compare `from` against `to`
   * inside an array - the overview filters these rows away and only counts them.
   */
  @Field(() => Boolean, { default: v => v ?? false })
  unchanged: Maybe<boolean>;

  /**
   * Whether the row is in the overview's table: it writes something, or its person is stored more than
   * once (see {@link duplicateMatchIDs}). Only ever turns on: a row the user is working through
   * must not drop out because an edit made it write nothing - the table pages with skip/limit, and a
   * row leaving the result set between two pages makes the next page skip one it never showed.
   */
  @Field(() => Boolean, { default: v => v ?? false })
  listed: Maybe<boolean>;

  /**
   * Every finding code the row has had in this process, only ever growing. The overview filters by this
   * rather than by the live findings, so a row whose email was just fixed stays in the "Ungültige E-Mail"
   * list - marked as fixed - instead of vanishing under the cursor. Same reasoning as {@link listed}.
   */
  @Field(() => [String], { default: v => v ?? [] })
  reviewCodes: Maybe<ImportFindingCode[]>;

  /**
   * The worst severity the row has had in this process - the severity filter's counterpart of
   * {@link reviewCodes}.
   */
  @Field(() => Float, { default: v => v ?? ImportFindingSeverity.HINWEIS })
  peakSeverity: Maybe<ImportFindingSeverity>;

  /**
   * The row as the csv said it, plus the user's edits in the overview. Is used to calculate the fieldSpecifiers.
   * Keys the matcher ignored stay in here, because every recalculation matches again and has to see them
   * again; only the specifiers leave them out, and the executor writes nothing but the specifiers.
   */
  @Field(() => PersonImportData)
  rawImportData: Maybe<PersonImportData>;

  /**
   * Converted import strategy. Defines which field gets which value
   */
  @Field(() => [ImportFieldSpecifier], { default: v => v ?? [] })
  fieldSpecifiers: Maybe<ImportFieldSpecifier[]>;

  /**
   * Everything the import noticed about this row before it runs.
   */
  @Field(() => [ImportFinding], { default: v => v ?? [] })
  findings: Maybe<ImportFinding[]>;

  /**
   * The worst severity across {@link findings}, denormalized so mongo can sort the overview by it.
   */
  @Field(() => Float, { default: v => v ?? ImportFindingSeverity.HINWEIS })
  severity: Maybe<ImportFindingSeverity>;

  /**
   * Execution Error of the import
   */
  @Field(() => String)
  error: Maybe<string>;
}

/**
 * The specifiers that actually write something. The executor applies all of them, but one whose value
 * already matches the database changes nothing.
 */
export const changedFieldSpecifiers = (specifiers: Maybe<ImportFieldSpecifier[]>): ImportFieldSpecifier[] =>
  (specifiers ?? []).filter(specifier => specifier.from !== specifier.to);

/**
 * See {@link PersonImportStrategy.unchanged}. Kept next to the field so every writer of a strategy
 * decides this the same way.
 */
export const isUnchangedStrategy = (strategy: PersonImportStrategy): boolean =>
  !!strategy.resolvedId && changedFieldSpecifiers(strategy.fieldSpecifiers).length === 0;

/**
 * Folds the row's current findings into what the overview filters by - see
 * {@link PersonImportStrategy.reviewCodes}. Every writer of a strategy calls this after its findings.
 */
export function recordReview(strategy: PersonImportStrategy): void {
  const codes = (strategy.findings ?? []).map(finding => finding.code).filter(code => !!code) as ImportFindingCode[];
  strategy.reviewCodes = [...new Set([...(strategy.reviewCodes ?? []), ...codes])];
  strategy.peakSeverity = Math.max(strategy.peakSeverity ?? ImportFindingSeverity.HINWEIS, strategy.severity ?? 0);
  /**
   * A row with duplicates is shown even when it writes nothing to its person: the executor still links the
   * duplicates to the children, and the duplicates themselves are what the user has to clean up.
   */
  strategy.listed = !!strategy.listed || !strategy.unchanged || !!strategy.duplicateMatchIDs?.length;
}
